import React from "react";
import PropTypes from "prop-types";
import { QuestionWrapper } from "../Components/QuestionWrapper";
import { Question } from "../Components/Question";
import { MainTitle } from "../Components/MainTitle";
import { QuestionImage } from "../Components/QuestionImage";
import { Utilities } from "@learningblocks/exp-constants";
import { Footer } from "../Components/Footer";
import { SubmitButton } from "../Components/SubmitButton";
import { TotalAttempts } from "../Components/TotalAttempts";
import { useIntl } from "react-intl";
import { NotebookImage } from "@learningblocks/simulations/shared-components";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import "./style.scss";

const { QUESTION_CONTENTS } = Utilities;

/**
 * OpenEnded [OEQ]:
 *  - Response Area
 *  - All answers are correct.
 */
export function OpenEnded(props) {
  const intl = useIntl();

  const setSubmitDisabled = () => {
    if (props.complete) return true;
    return false;
  };

  const onSubmit = () => props.submitOEQ(props.id);

  const onTextChange = (e) => {
    props.updateOEQ({
      componentID: props.id,
      text: e.target.value,
    });
  };

  const renderQuestion = (key, question) => (
    <Question
      tabIndex={"0"}
      key={key}
      text={question}
      className="oeq__question"
    />
  );

  const renderText = (key, text) => (
    <Question tabIndex={"0"} key={key} text={text} className="oeq__content" />
  );

  const renderImage = (key, image) => (
    <QuestionImage
      tabIndex={"0"}
      key={key}
      altText={props.altText}
      image={image}
      className="oeq__image"
      preview={props.preview}
    />
  );

  const figcaption = (key, content) => {
    return (
      <div style={{ marginBottom: "16px" }}>
        <NotebookImage
          key={key}
          {...{ image: content }}
          getGlossaryItem={props.getGlossaryItem}
          openGlossary={props.openGlossary}
        />
      </div>
    );
  };

  const renderResponseArea = () => {
    return (
      <div className="oeq__responseArea">
        <textarea
          rows="4"
          className="oeq__textarea-input"
          placeholder={intl.formatMessage({
            id: "Answer here.",
            defaultMessage: "Answer here.",
            description: "",
          })}
          onChange={onTextChange}
          defaultValue={props.oeqText}
        ></textarea>
      </div>
    );
  };

  const renderFeedback = () => {
    let feedback = props.feedback;
    if (props.resubmitted) {
      feedback = props.feedbackResubmitted
        ? props.feedbackResubmitted
        : props.feedback;
    }
    return (
      props.complete && (
        <div className="oeq__feedback">
          {typeof props.feedback === "string" && props.feedback}
          {typeof props.feedback === "object" && <RichText {...feedback} />}
        </div>
      )
    );
  };

  const renderByType = (type, key, content) => {
    switch (type) {
      case QUESTION_CONTENTS.QUESTION:
        return renderQuestion(key, content);
      case QUESTION_CONTENTS.TEXT:
        return renderText(key, content);
      case QUESTION_CONTENTS.IMAGE:
        return renderImage(key, content);
      case QUESTION_CONTENTS.FIGCAPTION:
        return figcaption(key, content);
      case QUESTION_CONTENTS.RESPONSE_AREA:
        return renderResponseArea();
      case QUESTION_CONTENTS.FEEDBACK:
        return renderFeedback();
      case QUESTION_CONTENTS.CONTENT_LIST:
        return content.map((listItem, subKey) =>
          renderByType(listItem.type, `${key}_${subKey}`, listItem.content)
        );
      default:
        return null;
    }
  };

  const wrapperClass = `oeq ${props.className}`;

  return (
    <QuestionWrapper className={wrapperClass}>
      <MainTitle
        title={intl.formatMessage({
          id: "OPEN-ENDED QUESTION",
          defaultMessage: "OPEN-ENDED QUESTION",
          description: "",
        })}
        className="oeq__mainTitle"
      />
      {props.content.map((type, key) => renderByType(type, key, props[type]))}
      <Footer className="oeq__footer">
        <TotalAttempts
          totalAttempts={props.totalAttempts}
          className="oeq__footer__totalAttempts"
        />
        {props.totalAttempts === 0 ? (
          <SubmitButton
            label={intl.formatMessage({
              id: "Submit answer",
              defaultMessage: "Submit answer",
              description: "",
            })}
            disabled={setSubmitDisabled()}
            onSubmit={onSubmit}
          />
        ) : (
          <SubmitButton
            label={intl.formatMessage({
              id: "Submit answer",
              defaultMessage: "Submit answer",
              description: "",
            })}
            onSubmit={onSubmit}
          />
        )}
      </Footer>
    </QuestionWrapper>
  );
}

OpenEnded.propTypes = {
  /**
   * The custom classname you wanna inject on the root of the component
   */
  className: PropTypes.string,
  /**
   * The prop to set if the component is complete
   */
  complete: PropTypes.bool.isRequired,
  /**
   * List of static contents of type question, text, and image you want to display
   */
  contentList: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Type of content Item
       */
      type: PropTypes.oneOf(["question", "image", "text"]),
      /**
       * Content Item assicated with the type
       */
      content: PropTypes.string,
    })
  ),
  /**
   * The prop to specify all contents in the question
   */
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * The prop that hold the unique id for the component
   */
  id: PropTypes.number,
  /**
   * The prop that hold image data
   */
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * The prop that holds the text for the question
   */
  question: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  /**
   * The prop that holds the total attempts,
   */
  totalAttempts: PropTypes.number.isRequired,
  /**
   * The prop that holds the Component type.
   */
  type: PropTypes.oneOf(["OEQ"]),
  /**
   * OEQ Text
   */
  oeqText: PropTypes.string,
  /**
   * The callback prop executed when submit is clicked
   */
  submitOEQ: PropTypes.func.isRequired,
  /**
   * Submit Button Label
   */
  submitLabel: PropTypes.string,
  /**
   * The callback prop executed when submit is clicked
   */
  resetAnswer: PropTypes.func.isRequired,
};

OpenEnded.defaultProps = {
  className: "",
  type: "OEQ",
  oeqText: "",
  complete: false,
  resubmitted: false,
  content: [],
  image: "",
  question: "",
  totalAttempts: 0,
  caption: "",
  preview: false,
  submitLabel: "Submit answer",
};
